import { useState, useEffect } from "react";
import { AdminLink, OwnerLink } from "@/components/layout/sidebar/types";
import { supabase } from "@/integrations/supabase/client";

interface AccessControl {
  feature_name: string;
  access_code: string;
  visibility_duration_minutes: number;
  last_verified_at: string;
  is_disabled: boolean;
}

export const useMenuVisibility = (
  accessControls: AccessControl[] | undefined,
  links: (AdminLink | OwnerLink)[],
  refetch: () => void
) => {
  const [visibleMenuItems, setVisibleMenuItems] = useState<string[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Vérifier et mettre à jour la visibilité en fonction de la durée
  useEffect(() => {
    if (!accessControls) return;

    const checkVisibility = () => {
      const currentTime = new Date();
      const updatedVisibleItems = links.reduce<string[]>((acc, link) => {
        // Si c'est un lien admin ou qu'il n'a pas de feature, toujours visible
        if (!('feature' in link) || !link.feature) {
          acc.push(link.name);
          return acc;
        }

        const control = accessControls.find(ac => ac.feature_name === link.feature);
        
        // Si pas de contrôle d'accès ou si désactivé, toujours visible
        if (!control || control.is_disabled) {
          acc.push(link.name);
          return acc;
        }

        // Si jamais vérifié, pas visible
        if (!control.last_verified_at) {
          return acc;
        }

        const lastVerified = new Date(control.last_verified_at);
        const durationInMinutes = control.visibility_duration_minutes || 17;
        const expirationTime = new Date(lastVerified.getTime() + durationInMinutes * 60000);

        if (currentTime < expirationTime) {
          acc.push(link.name);
        }

        return acc;
      }, ['Tableau de bord']); // Toujours inclure le tableau de bord

      setVisibleMenuItems(updatedVisibleItems);
    };

    // Vérification initiale
    checkVisibility();

    // Vérification périodique toutes les minutes
    const interval = setInterval(checkVisibility, 60000);

    return () => clearInterval(interval);
  }, [accessControls, links]);

  const toggleMenuItemVisibility = async (name: string) => {
    const link = links.find(l => l.name === name);
    if (!link || !('feature' in link) || !link.feature) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const now = new Date().toISOString();
    const { error } = await supabase
      .from('access_controls')
      .update({ last_verified_at: now })
      .eq('owner_id', user.id)
      .eq('feature_name', link.feature);

    if (error) {
      console.error('Error updating verification time:', error);
      return;
    }

    setVisibleMenuItems(prev => 
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name]
    );

    refetch();
  };

  return {
    visibleMenuItems,
    selectedMenuItem,
    isDialogOpen,
    setIsDialogOpen,
    setSelectedMenuItem,
    toggleMenuItemVisibility,
  };
};